import React, { forwardRef, useState, useEffect, useContext, useImperativeHandle } from 'react'
import { TextField } from '@strata-monorepo/shared-ui/components/TextField/TextField'
import type { FormStepProps } from '@redventures/odyssey'
import { OdysseyContext } from '@redventures/odyssey'
import useDebounce from '@strata-monorepo/shared-ui/utils/useDebounce'
import getStateByZip from '@strata-monorepo/shared-ui/utils/api/getStateByZip'
import apolloClient from '@strata-monorepo/shared-ui/utils/Apollo/ApolloClient'
import { withRetry } from '@strata-monorepo/shared-ui/utils/retryUtil'
import { MapPin, CircleAlert } from 'lucide-react'
import type { FormStepRef } from './formStepRef'
import type { CohesionGlobal } from '@strata-monorepo/shared-types/src/window'
import { useStoreActions } from '../../store/useStore'
import { Toggle } from '@strata-monorepo/shared-ui/components/Toggle/Toggle'
import { mapErcotToAddress } from '../../utils/mapErcotToAddress'
import type { ErcotAddress } from '../../utils/mapErcotToAddress'
import { formatAddress } from '@strata-monorepo/shared-ui/utils/formatAddress'
import type { Address } from '@strata-monorepo/shared-ui/types/Address'
import validateEnrichedCustomerPropertyData from '@strata-monorepo/utils/validateEnrichedCustomerPropertyData'
import { useIncense } from '../../hooks/useIncense'
import useFieldInputted from '@strata-monorepo/tracking/src/hooks/useFieldInputted'
import useFieldSelected from '@strata-monorepo/tracking/src/hooks/useFieldSelected'
import { useStoreState } from '../../store/useStore'
import useFormContinued from '@strata-monorepo/tracking/src/hooks/useFormContinued'
import { mapSquareFootageToSize } from '../../utils/mapSquareFootageToSize'
import { getSuggestedAddresses } from '@strata-monorepo/find-my-plan/graphql/suggestedAddresses'
import { QuestionFlowEnum } from '@strata-monorepo/shared-types'
import {
  isGraphQlErrorResponse,
  traceRequest,
} from '@strata-monorepo/utils/telemetry/requestTrace/requestTrace'

const moverOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

interface AddressAndMoverComponentProps {
  formValue: string
  shouldFetchConsumptionPrediction?: boolean
  movingFieldKey?: 'moving' | 'movingStates' | null
  tracking?: {
    stepName?: string
    stepId?: string
    stepNumber?: number
    fieldName?: string
  }
}

interface AddressSelection {
  street_line: string
  secondary?: string
  city: string
  state: string
  zipcode: string
  enriched: boolean
}

interface AddressOption {
  readonly value: Address
  readonly label: string
  readonly premiseType: string
}

const MIN_ADDRESS_LENGTH = 4

const AddressAndMover = forwardRef<FormStepRef, FormStepProps>(({ formStep }, ref) => {
  const [options, setOptions] = useState<AddressOption[]>([])

  const odysseyContext = useContext(OdysseyContext)
  const [addressInput, setAddressInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [isNotResidential, setIsNotResidential] = useState(false)

  const { metricsClient } = useStoreState((state) => state.metrics)
  const { formVersion, formId, currentStepIndex } = useStoreState((state) => state.form)
  const formType = useStoreState((state) => state.form.formType)

  const { componentProps } = formStep
  const { tracking, shouldFetchConsumptionPrediction, movingFieldKey } =
    componentProps as AddressAndMoverComponentProps

  const trackFieldInputted = useFieldInputted()
  const trackFieldSelected = useFieldSelected()
  const trackFormContinued = useFormContinued()

  const fetchEnrichCustomerData = useStoreActions(
    (actions) => actions.enrichCustomer.fetchEnrichCustomerData,
  )

  const fetchConsumptionPrediction = useStoreActions(
    (actions) => actions.consumptionPrediction.fetchConsumptionPrediction,
  )

  const incense = useIncense()

  // Using a debounced value to prevent too many API calls
  const debouncedInputValue = useDebounce(addressInput, 500) || ''

  const triggerEvent = (
    step: number,
    fieldName: string,
    fieldType: string,
    eventName: typeof trackFieldInputted | typeof trackFieldSelected,
    value?: string,
  ) => {
    if (!tracking) return

    eventName({
      formContext: {
        formName: formVersion,
        formType,
        formId,
      },
      stepContext: {
        stepId: String(step || 0),
        stepName: tracking?.stepName || 'Address Entry',
        stepNumber: step || 0,
      },
      userInputField: {
        autofilled: true,
        fieldName,
        fieldType,
        fieldValue: value || 'unknown',
        fieldSource: 'manual',
      },
    })
  }

  const clickHandler = (option: AddressOption): void => {
    setShowSuggestions(false)
    setOptions([])

    handleChange(option)

    const isResidential = option.premiseType === 'Residential'
    setIsNotResidential(!isResidential)
  }

  const handleChange = (selectedAddress: AddressOption) => {
    if (selectedAddress?.label) {
      setAddressInput(selectedAddress.label)
      odysseyContext?.setValue('address', selectedAddress.value)
      triggerEvent(
        currentStepIndex || 0,
        'address',
        'text',
        trackFieldInputted,
        JSON.stringify(selectedAddress),
      )
    }
  }

  const trackEnrichedHomeDetails = (
    squareFootage?: number | null,
    bedrooms?: number | null,
    bathrooms?: number | null,
    floorCount?: number | null,
    yearBuilt?: number | null,
  ) => {
    trackFormContinued({
      field: [
        {
          fieldName: 'homeInfo.homeSize',
          fieldLabel: 'homeInfo.homeSize',
          fieldValue: squareFootage ? mapSquareFootageToSize(squareFootage) : '',
        },
        {
          fieldName: 'homeInfo.numBedrooms',
          fieldLabel: 'homeInfo.numBedrooms',
          fieldValue: bedrooms ? String(bedrooms) : '',
        },
        {
          fieldName: 'homeInfo.numBathrooms',
          fieldLabel: 'homeInfo.numBathrooms',
          fieldValue: bathrooms ? String(bathrooms) : '',
        },
        {
          fieldName: 'homeInfo.numFloors',
          fieldLabel: 'homeInfo.numFloors',
          fieldValue: floorCount ? String(floorCount) : '',
        },
        {
          fieldName: 'homeInfo.yearBuilt',
          fieldLabel: 'homeInfo.yearBuilt',
          fieldValue: yearBuilt ? String(yearBuilt) : '',
        },
      ],
      formContext: {
        formName: formVersion,
        formId,
        formType,
      },
      stepContext: {
        stepId: '1.1',
        stepName: 'Home Details Rentcast',
        stepNumber: 1,
      },
    })
  }

  const buildAlternativeGridUrl = (zipCode: string, state: string | null): string | null => {
    if (state === null || state !== 'TX') {
      return `https://www.chooseenergy.com/shop/residential/electricity/${state}/${zipCode}?isNTX=true`
    }

    return null
  }

  // Expose callback function to parent via ref
  useImperativeHandle(ref, () => ({
    onBeforeNext: async () => {
      setHasAttemptedSubmit(true)
      const cohesionData: CohesionGlobal = (window as Window)._Cohesion || {}
      const addressData = odysseyContext?.getValue('address') as AddressSelection | undefined
      const interstitialValue = odysseyContext?.getValue('interstitial')

      if (isNotResidential) {
        return false
      }

      if (!addressData || !addressData.zipcode) {
        // Allows Odyssey form validation to still continue if no address is selected
        return true
      }

      const stateAbbreviation = await getStateByZip(addressData.zipcode, incense)

      if (stateAbbreviation?.toUpperCase() != 'TX') {
        const targetUrl = buildAlternativeGridUrl(addressData.zipcode, stateAbbreviation)

        // Redirect to the alternative grid url for NTX zip codes
        if (targetUrl) {
          window.location.href = targetUrl
        }
      }

      try {
        const enrichCustomerResponse = await fetchEnrichCustomerData({
          CohesionGlobal: cohesionData,
          address: addressData as Address,
        })

        // (a) null — real network/API failure path (thunk swallows errors and returns null)
        if (!enrichCustomerResponse) {
          incense(null)
            .captureDetails({
              name: 'Questionnaire.AddressAndMover.EnrichCustomerNull',
              message: 'Find My Plan questionnaire | Enrich Customer Data returned null',
            })
            .sendError()
          await odysseyContext?.setValue('address.enriched', false)
          return true
        }

        const propertyData = enrichCustomerResponse.results?.propertyData

        // (b) response present but propertyData missing — malformed response
        if (!propertyData) {
          incense(null)
            .captureDetails({
              name: 'Questionnaire.AddressAndMover.MissingPropertyData',
              message: 'Find My Plan questionnaire | Enrich Customer response missing propertyData',
            })
            .sendError()
          await odysseyContext?.setValue('address.enriched', false)
          return true
        }

        // (c) propertyData present but incomplete or imputed — expected product outcome, no error
        if (!validateEnrichedCustomerPropertyData(propertyData)) {
          await odysseyContext?.setValue('address.enriched', false)
          return true
        }

        const { bedrooms, bathrooms, features, squareFootage, yearBuilt } = propertyData
        const floorCount = features?.floorCount

        trackEnrichedHomeDetails(squareFootage, bedrooms, bathrooms, floorCount, yearBuilt)

        await odysseyContext?.setValue('address.enriched', true)

        // Fetch consumption prediction if configured to do so
        // (e.g., quick flow where HomeInfo step is skipped)
        // If prop is undefined, fallback to checking interstitial value (for default questionnaire)
        const shouldFetch =
          shouldFetchConsumptionPrediction ?? interstitialValue === QuestionFlowEnum.QUICK

        if (shouldFetch) {
          await fetchConsumptionPrediction({
            cohesionData,
            enrichedData: enrichCustomerResponse,
            allEntries: [],
          })
        }

        return true
      } catch (error) {
        // Genuine thrown errors (should not normally occur since thunk swallows errors)
        incense(error)
          .captureDetails({
            name: 'Questionnaire.AddressAndMover.Error',
            message: 'Find My Plan questionnaire | Enrich Customer Data API Request Error',
          })
          .sendError()

        await odysseyContext?.setValue('address.enriched', false)
        return true
      }
    },
  }))

  // Fetch address suggestions from internal GraphQL (esiidAddress)
  useEffect(() => {
    let isCancelled = false

    const fetchSuggestions = async (val: string): Promise<void> => {
      if (!showSuggestions || val.length < MIN_ADDRESS_LENGTH || !debouncedInputValue) {
        setOptions([])
        return
      }

      // Traced outside withRetry so one lookup is one outcome, not one per attempt
      const addressTrace = traceRequest(metricsClient, 'suggestedAddresses')

      try {
        const { result: response } = await withRetry(
          async () => {
            return await apolloClient.query({
              query: getSuggestedAddresses,
              variables: { address: val, zipCode: '' },
              fetchPolicy: 'network-only',
            })
          },
          {
            maxRetries: 3,
            baseDelay: 500,
            logAttempts: false,
          },
          {
            requestName: 'fmpFetchSuggestedAddresses',
            metricsClient: metricsClient,
          },
        )

        // Settled before the cancel check so an abandoned lookup still reports what the server returned,
        // matching the failure path; otherwise only abandoned failures would reach the success rate
        // An empty list is a valid answer; a missing result is not
        if (response?.data?.esiidAddress == null) addressTrace.invalid()
        else addressTrace.succeed()

        if (isCancelled) return

        const ercotResults: ErcotAddress[] = response?.data?.esiidAddress ?? []
        const formattedOptions: AddressOption[] = ercotResults.map((ercotAddress) => {
          const address = mapErcotToAddress(ercotAddress)
          return {
            value: address,
            label: formatAddress(address),
            premiseType: ercotAddress.premiseType,
          }
        })

        setOptions(formattedOptions)
      } catch (error) {
        addressTrace.fail()

        if (isCancelled) return

        incense(error)
          .captureDetails({
            name: 'Questionnaire.AddressAndMover.Error',
            message: 'Find My Plan questionnaire | getSuggestedAddresses Request Error',
          })
          .sendError()

        setOptions([])
      }
    }

    fetchSuggestions(debouncedInputValue)

    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue, showSuggestions])

  useEffect(() => {
    const address = odysseyContext?.getValue('address') as Address | undefined
    // Only display a full address — partial prefills (e.g. zipcode-only) must not produce malformed strings
    if (address?.street_line) {
      setAddressInput(formatAddress(address))
    }
  }, [odysseyContext])

  const checkAddressError = (): React.ReactNode => {
    const addressError = odysseyContext?.getError('address')
    if (addressError && hasAttemptedSubmit) {
      if (addressInput.length > 0) {
        return (
          <>
            Sorry, we do not currently service that address. For questions, please call{' '}
            <a
              href='tel:8443666785'
              className='strata:underline'
            >
              (844-366-6785)
            </a>
            .
          </>
        )
      }
      return 'Address is required.'
    }
    return undefined
  }

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddressInput(event.target.value)
    setShowSuggestions(true)
    setHasAttemptedSubmit(false)
    odysseyContext?.setValue('address', undefined)
  }

  const movingQuestion = (questionType: 'moving' | 'movingStates') => {
    const isMovingStates = questionType === 'movingStates'
    const toggleLabel = isMovingStates
      ? 'Are you moving to Texas from another state?'
      : 'Are you moving to a new address?'

    const errorMessage = odysseyContext?.getError(questionType)

    const handleToggleChange = (values: string[]) => {
      odysseyContext?.setValue(questionType, values[0])
      triggerEvent(
        currentStepIndex || 0,
        `${questionType}_new`,
        'button',
        trackFieldSelected,
        values[0],
      )
    }

    return (
      <>
        <Toggle
          variant='outlined'
          label={toggleLabel}
          options={moverOptions}
          values={[odysseyContext?.getValue(questionType) as string]}
          onChange={handleToggleChange}
          className='strata:text-left'
        />

        {errorMessage && (
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {errorMessage}
          </p>
        )}
      </>
    )
  }

  return (
    <>
      <div className='strata:relative'>
        <div>
          <TextField
            value={addressInput}
            onChange={handleTextFieldChange}
            onDoubleClick={(event: React.MouseEvent<HTMLInputElement>) => {
              event.currentTarget.select()
            }}
            placeholder='Enter home address'
            label='Address'
            id='address'
            autoComplete='off'
            icon={<MapPin className='strata:h-3 strata:w-3 strata:text-gray-400' />}
            error={checkAddressError()}
          />
        </div>
        {showSuggestions && options.length > 0 && (
          <ul className='strata:absolute strata:z-20 strata:w-full strata:border strata:border-gray-200 strata:rounded-sm strata:p-1 strata:bg-white'>
            {options.map((option) => (
              <li
                key={option.label}
                className='strata:list-none'
              >
                <button
                  type='button'
                  onClick={() => clickHandler(option)}
                  className='strata:w-full strata:cursor-pointer strata:text-left strata:text-body-sm strata:p-2 strata:rounded-sm strata:hover:bg-secondary-50 strata:hover:font-bold'
                  aria-label={`Select address ${option.label}`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        {isNotResidential && (
          <div className='strata:flex strata:gap-2 strata:@qm/qc:gap-3 strata:bg-system-warning-light strata:text-left strata:py-2 strata:px-3 strata:@qm/qc:py-3 strata:@qm/qc:px-4 strata:rounded-tl-none strata:rounded-bl-lg strata:rounded-r-sm'>
            <CircleAlert className='strata:flex-shrink-0 strata:h-3 strata:w-3 strata:@qm/qc:h-4 strata:@qm/qc:w-4 strata:text-system-warning-dark' />
            <div className='strata:flex-1'>
              <p className='strata:text-body-xs strata:@qm/qc:text-body-sm strata:font-bold'>
                We’re sorry! We only offer residential service options at this time.
              </p>
              <p className='strata:text-body-xs strata:@qm/qc:text-body-sm'>
                Please visit{' '}
                <a
                  href='https://www.chooseenergy.com/'
                  className='strata:font-bold'
                >
                  ChooseEnergy
                </a>{' '}
                to explore energy plans for your business or organization.
              </p>
            </div>
          </div>
        )}
      </div>

      {movingFieldKey && movingQuestion(movingFieldKey)}
    </>
  )
})

AddressAndMover.displayName = 'AddressAndMover'

export default AddressAndMover
